import * as React from 'react';

import {
  DrawerContext,
  FilterChip,
  IconRegularClose,
} from '@notino/react-styleguide';
import { WishlistFiltrationGroupType } from '@notino/shared/definitions/types';

import { OverflowFade } from '@components/OverflowFade';
import { useWishlistStore } from '@containers/WishList/store';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { trackRemoveFiltration, trackSelectFiltration } from '../tracking';

import { Categories } from './Categories';
import { FiltrationDrawerHandler } from './FiltrationDrawerHandler';
import { SortingChip } from './SortingChip';
import {
  ChipCount,
  FilterChipsWrapper,
  FiltersSectionWrapper,
  InnerWrapper,
} from './styled';
import { useGetSelectedFilterItems } from './utils';

const SUPPORTED_GROUP_TYPES = [
  WishlistFiltrationGroupType.Brand,
  WishlistFiltrationGroupType.ForWho,
  WishlistFiltrationGroupType.FreeGiftsAndOffers,
];

export const FiltersSection = () => {
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const { getTimeFromInit } = useTrackingContext();

  const { setSelectedNavigationValues } = useWishlistStore((x) => x.actions);
  const filtration = useWishlistStore((x) => x.filtration).filter((x) =>
    SUPPORTED_GROUP_TYPES.includes(x.groupType)
  );
  const selectedNavigationValues = useWishlistStore(
    (x) => x.selectedNavigationValues
  );

  const getSelectedFilterItems = useGetSelectedFilterItems();

  const [openedDrawerGroupType, setOpenedDrawerGroupType] =
    React.useState<WishlistFiltrationGroupType>();

  const onChipClick = (
    groupType: WishlistFiltrationGroupType,
    groupName: string
  ) => {
    // NOTE: cuz of drawer close timeout when clicking like crazy on filter chip
    // drawer got closed but "openedDrawerGroupType" was still set
    // so I added this ternary to set openedDrawerGroupType to undefined
    setOpenedDrawerGroupType(openedDrawerGroupType ? undefined : groupType);

    trackSelectFiltration(getTimeFromInit(), groupName);
  };

  const onCloseClick = (
    items: NonNullable<ReturnType<typeof getSelectedFilterItems>>,
    groupName: string
  ) => {
    setSelectedNavigationValues(
      selectedNavigationValues.filter(
        (x) => !items.some((i) => i.navigationValue === x)
      )
    );

    trackRemoveFiltration(getTimeFromInit(), groupName);
  };

  return (
    <>
      {openedDrawerGroupType && (
        <FiltrationDrawerHandler
          groupType={openedDrawerGroupType}
          onClose={() => {
            setOpenedDrawerGroupType(undefined);
            closeDrawer();
          }}
        />
      )}

      <FiltersSectionWrapper>
        <InnerWrapper>
          <Categories />

          <OverflowFade config={{ showArrowCalulationOffset: 12 }}>
            <FilterChipsWrapper>
              {filtration.map((f) => {
                const items = getSelectedFilterItems(f.groupType) ?? [];
                const count = items.length;

                return (
                  <FilterChip
                    key={f.groupType}
                    isActive={count > 0}
                    title={
                      count ? (
                        <>
                          {items[0].name}
                          {count > 1 && <ChipCount>+{count - 1}</ChipCount>}
                        </>
                      ) : (
                        f.groupName
                      )
                    }
                    secondaryIcon={
                      <IconRegularClose width="16px" height="16px" />
                    }
                    onSecondaryIconClick={
                      count > 0
                        ? () => onCloseClick(items, f.groupName)
                        : undefined
                    }
                    onClick={() => {
                      onChipClick(f.groupType, f.groupName);
                    }}
                  />
                );
              })}

              <SortingChip />
            </FilterChipsWrapper>
          </OverflowFade>
        </InnerWrapper>
      </FiltersSectionWrapper>
    </>
  );
};
